import React, { useRef, useState, useEffect } from "react";
import { UploadCloud, Loader2, Film, Trash2, CheckCircle2 } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";

export default function VideoUpload({ label, value, onChange, hint }) {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [optimizing, setOptimizing] = useState(false);
  const [ready, setReady] = useState(false);
  const pollRef = useRef(null);

  const clearPoll = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };

  const pollStatus = async (val) => {
    if (!val) return;
    try {
      const res = await fetch(mediaUrl(`${val}/status`));
      if (res.ok) {
        const data = await res.json();
        if (data.optimized) {
          setOptimizing(false);
          setReady(true);
          clearPoll();
          return;
        }
      }
    } catch (e) { /* ignore, keep polling */ }
    setOptimizing(true);
    pollRef.current = setTimeout(() => pollStatus(val), 3000);
  };

  // Start/refresh polling whenever the video reference changes.
  useEffect(() => {
    clearPoll();
    setReady(false);
    setOptimizing(false);
    if (value) pollStatus(value);
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast({ title: "Please choose a video file", variant: "destructive" }); return; }
    if (file.size > 300 * 1024 * 1024) { toast({ title: "Video too large", description: "Max 300MB", variant: "destructive" }); return; }
    setUploading(true);
    setProgress(0);
    try {
      const res = await adminApi.uploadVideo(file, setProgress);
      onChange(res.url);
      setOptimizing(true);
      setReady(false);
      toast({ title: "Video uploaded", description: "Optimising for fast playback — remember to save." });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const posterUrl = value ? mediaUrl(`${value}/poster`) : "";

  return (
    <div>
      {label && <Label className="text-navy font-semibold">{label}</Label>}
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      <div className="mt-2 flex items-start gap-4">
        <div className="w-40 aspect-video rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <video src={mediaUrl(value)} poster={posterUrl || undefined} className="w-full h-full" controls preload="metadata" />
          ) : <Film className="text-slate-300" size={24} />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="video/*" onChange={handleFile} className="hidden" />
          <Button type="button" variant="outline" data-testid="service-video-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-full">
            {uploading ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Uploading {progress}%</> : <><UploadCloud size={15} className="mr-1.5" /> {value ? "Replace video" : "Upload video"}</>}
          </Button>
          {value && optimizing && !uploading && (
            <span data-testid="video-optimizing-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 w-max">
              <Loader2 size={12} className="animate-spin" /> Optimising for fast playback…
            </span>
          )}
          {value && ready && !uploading && (
            <span data-testid="video-ready-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2.5 py-1 w-max">
              <CheckCircle2 size={12} /> Optimised &amp; ready
            </span>
          )}
          {value && !uploading && (
            <button type="button" data-testid="service-video-remove-btn" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline text-left">
              <Trash2 size={12} /> Remove
            </button>
          )}
          {uploading && (
            <div className="w-40 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-orange transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
